import React, { useCallback, useMemo, useState } from 'react';

import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import styled, { css } from 'styled-components';

import {
  WaitlistType,
  useWaitlistIds,
} from '~/techstyle-shared/react-accounts';
import {
  Button,
  Collapsible,
  useCollapsibleState,
  useProductDetailSelectionState,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import QuickView from '../QuickView';
import WaitlistModal from '../WaitlistModal';

import { useProductDetailCTAContext } from './ProductDetailCTAContext';

const Wrapper = styled.div``;

const WaitlistButton = styled(Button)`
  width: 100%;
`;

const labelStyle = css`
  font-size: 18px;
  font-weight: bold;
  letter-spacing: ${-0.4 / 18}em;
  color: ${({ theme }) => theme.colors.textLight};
`;

const IconWrapper = styled.span`
  margin-left: 4px;
  vertical-align: middle;
`;

const CollapsibleContent = styled(Collapsible.Content)`
  margin-top: 16px;
`;

function WaitlistCTA(props) {
  const {
    addToWaitlist,
    getBundleProductIds,
    getProductId,
    handleWaitlistValidation,
    hasWaitlistError,
    isBundle,
    deleteFromCart,
  } = useProductDetailCTAContext();
  const { isPlusSizeSelected } = useProductDetailSelectionState();
  const productDetailContext = useProductDetailContext();
  const { networkStatus, hasProductId } = useWaitlistIds();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const collapsibleState = useCollapsibleState();
  let isOpen;
  let toggleCollapsible;
  if (collapsibleState) {
    isOpen = collapsibleState.isOpen;
    toggleCollapsible = collapsibleState.toggleCollapsible;
  }
  const QuickViewContext = QuickView.useContext();

  const handleModalOpen = useCallback((event) => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onCTAClick = useCallback(async () => {
    try {
      setIsLoading(true);
      await handleWaitlistValidation();
      await addToWaitlist({
        apiParams: {
          waitlistTypeId: WaitlistType.EMAIL_NOTIFICATION,
        },
      });
      if (QuickViewContext && QuickViewContext.isCheckout) {
        deleteFromCart(QuickViewContext.cartItem);
        collapsibleState.toggleCollapsible();
      } else if (collapsibleState) {
        collapsibleState.toggleCollapsible();
      } else {
        handleModalClose();
      }
    } catch (error) {
      // TODO: Custom error handling here
    }
    setIsLoading(false);
  }, [
    QuickViewContext,
    addToWaitlist,
    collapsibleState,
    deleteFromCart,
    handleModalClose,
    handleWaitlistValidation,
  ]);

  const isProductIdInWaitlist = useMemo(() => {
    if (!hasWaitlistError) {
      if (isBundle) {
        return getBundleProductIds(productDetailContext).bundleProductIds.every(
          (productId) => hasProductId(productId)
        );
      } else {
        return hasProductId(getProductId(productDetailContext));
      }
    } else {
      return true;
    }
  }, [
    getBundleProductIds,
    getProductId,
    hasWaitlistError,
    hasProductId,
    isBundle,
    productDetailContext,
  ]);

  const canAddToWaitlist = !isProductIdInWaitlist;
  const dialogProps = { 'data-autotag': 'pdp_waitlist_Modal_Dialog' };

  return (
    <Wrapper {...props}>
      {collapsibleState ? (
        <>
          <WaitlistButton
            onClick={toggleCollapsible}
            labelStyle={labelStyle}
            disabled={networkStatus.isLoading || !canAddToWaitlist}
            isLoading={networkStatus.isLoading}
            data-autotag="pdp-waitlist-cta"
          >
            {!canAddToWaitlist && !hasWaitlistError ? (
              <FormattedMessage
                id="site_pdp.on_waitlist_cta"
                defaultMessage="You're on the Waitlist"
              />
            ) : (
              <FormattedMessage
                id="site_pdp.join_waitlist_cta"
                defaultMessage="Join Waitlist"
              />
            )}
            {isOpen ? (
              <IconWrapper>
                <FaAngleUp size={14} />
              </IconWrapper>
            ) : (
              <IconWrapper>
                <FaAngleDown size={14} />
              </IconWrapper>
            )}
          </WaitlistButton>
          <CollapsibleContent isOpen={collapsibleState.isOpen}>
            <WaitlistModal
              dialogProps={dialogProps}
              onCTAClick={onCTAClick}
              isPlusSizeSelected={isPlusSizeSelected}
              isLoading={isLoading}
            />
          </CollapsibleContent>
        </>
      ) : (
        <>
          <WaitlistButton
            onClick={handleModalOpen}
            labelStyle={labelStyle}
            disabled={networkStatus.isLoading || !canAddToWaitlist}
            isLoading={networkStatus.isLoading}
            data-autotag="pdp_waitlist_cta"
          >
            {!canAddToWaitlist && !hasWaitlistError ? (
              <FormattedMessage
                id="site_pdp.on_waitlist_cta"
                defaultMessage="You're on the Waitlist"
              />
            ) : (
              <FormattedMessage
                id="site_pdp.join_waitlist_cta"
                defaultMessage="Join Waitlist"
              />
            )}
          </WaitlistButton>
          <WaitlistModal
            dialogProps={dialogProps}
            onCTAClick={onCTAClick}
            isOpen={isModalOpen}
            isPlusSizeSelected={isPlusSizeSelected}
            isLoading={isLoading}
            onExit={handleModalClose}
          />
        </>
      )}
    </Wrapper>
  );
}

export default WaitlistCTA;
